// src/pages/QRScannerPage.jsx
import React, { useRef, useState } from "react";
import QRScanner from "../components/QRScanner";
import axios from "../services/api";

export default function QRScannerPage() {
  const ref = useRef(null);

  const [scannedText, setScannedText] = useState("");
  const [ticketInfo, setTicketInfo] = useState(null);
  const [error, setError] = useState("");

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  const submitCode = async (code) => {
    setError("");
    setTicketInfo(null);
    try {
      const { data } = await axios.post(`/api/ticket-scan?qrCodeValue=${code}`);
      setTicketInfo(data);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data ||
        "Something went wrong while validating the ticket.";
      setError(String(msg));
    }
  };

  const handleScan = async (text) => {
    setScannedText(text);
    await submitCode(text);
  };

  const rescan = async () => {
    setScannedText("");
    setTicketInfo(null);
    setError("");
    await ref.current?.startScanner?.();
  };

  return (
    <div className="page max-w-md space-y-6">
      {/* Header */}
      <section className="card">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl">Ticket Scanner</h1>
          <span className="badge">Mobile</span>
        </div>
        <p className="mt-1 text-slate-300 text-sm">
          Point at the QR. The screen will beep & vibrate on a valid scan.
        </p>
      </section>

      {/* Scanner */}
      {!scannedText && !ticketInfo && !error && (
        <section className="card">
          <QRScanner ref={ref} onScan={handleScan} />
        </section>
      )}

      {/* Success */}
      {ticketInfo && (
        <section className="card bg-emerald-500/10 border-emerald-500/20">
          <div className="flex items-start justify-between">
            <h2 className="text-xl font-semibold text-white">✅ Ticket Valid</h2>
            <span className="badge bg-emerald-500/20 text-emerald-200">
              {ticketInfo.status || "OK"}
            </span>
          </div>

          <dl className="mt-3 grid grid-cols-1 gap-y-2 text-slate-300 text-sm">
            <div>
              <dt className="text-slate-400">Event</dt>
              <dd className="text-white font-medium">{ticketInfo.eventTitle}</dd>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <dt className="text-slate-400">Date</dt>
                <dd>{ticketInfo.eventDate}</dd>
              </div>
              <div>
                <dt className="text-slate-400">Time</dt>
                <dd>
                  {ticketInfo.startTime} – {ticketInfo.endTime}
                </dd>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <dt className="text-slate-400">Name</dt>
                <dd>
                  {ticketInfo.firstName} {ticketInfo.lastName}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">DOB</dt>
                <dd>{fmtDate(ticketInfo.dateOfBirth)}</dd>
              </div>
            </div>
            {ticketInfo.ticketType && (
              <div>
                <dt className="text-slate-400">Type</dt>
                <dd>{ticketInfo.ticketType}</dd>
              </div>
            )}
            {ticketInfo.ticketId && (
              <div>
                <dt className="text-slate-400">Ticket ID</dt>
                <dd className="font-mono text-slate-200">{ticketInfo.ticketId}</dd>
              </div>
            )}
          </dl>

          <div className="mt-4 flex gap-2">
            <button className="btn-primary flex-1" onClick={rescan}>
              Scan next ticket
            </button>
            <button
              className="btn-secondary flex-1"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Back to top
            </button>
          </div>
        </section>
      )}

      {/* Error */}
      {error && (
        <section className="card bg-red-500/10 border-red-500/20">
          <div className="flex items-start justify-between">
            <h2 className="text-xl font-semibold text-white">❌ Not Valid</h2>
            <span className="badge bg-red-500/20 text-red-200">Error</span>
          </div>
          <p className="mt-2 text-slate-200">{error}</p>
          {scannedText && (
            <p className="mt-2 text-xs text-slate-400 break-all">
              Code: <span className="font-mono">{scannedText}</span>
            </p>
          )}
          <div className="mt-4">
            <button className="btn-primary w-full" onClick={rescan}>
              Try again
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
